import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Module-level in-memory cache that survives route changes.
 * Each entry stores the data, a timestamp, and an optional ETag-style
 * fingerprint so callers can detect stale-but-usable data.
 */
interface CacheEntry<T> {
  data: T;
  savedAt: number;
  fingerprint?: string;
}

const store = new Map<string, CacheEntry<unknown>>();

const DEFAULT_STALE_MS = 60_000;
const DEFAULT_MAX_AGE_MS = 5 * 60_000;

function getEntry<T>(key: string): CacheEntry<T> | null {
  const entry = store.get(key) as CacheEntry<T> | undefined;
  if (!entry) return null;
  if (Date.now() - entry.savedAt > DEFAULT_MAX_AGE_MS) {
    store.delete(key);
    return null;
  }
  return entry;
}

function setEntry<T>(key: string, data: T, fingerprint?: string): void {
  store.set(key, { data, savedAt: Date.now(), fingerprint });
}

export function invalidatePageCache(keyPrefix?: string): void {
  if (!keyPrefix) {
    store.clear();
    return;
  }
  for (const key of Array.from(store.keys())) {
    if (key.startsWith(keyPrefix)) store.delete(key);
  }
}

export interface UsePageCacheOptions<T> {
  key: string;
  fetcher: (signal: AbortSignal) => Promise<T>;
  staleMs?: number;
  enabled?: boolean;
}

export interface UsePageCacheResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
  refresh: () => void;
  setData: (updater: T | ((prev: T | null) => T | null)) => void;
}

/**
 * Stale-while-revalidate hook for page-level data.
 *
 * - On mount, immediately returns cached data (no spinner).
 * - If cache is stale (>staleMs), kicks off a background fetch.
 * - If no cache, shows loading state and fetches.
 * - `setData` allows optimistic local mutations without re-fetching.
 * - `refresh` forces a re-fetch regardless of staleness.
 */
export function usePageCache<T>({
  key,
  fetcher,
  staleMs = DEFAULT_STALE_MS,
  enabled = true,
}: UsePageCacheOptions<T>): UsePageCacheResult<T> {
  const cached = getEntry<T>(key);
  const isFresh = cached ? Date.now() - cached.savedAt < staleMs : false;

  const [data, setDataState] = useState<T | null>(cached?.data ?? null);
  const [loading, setLoading] = useState(!cached && enabled);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(cached ? !isFresh : false);
  const [refreshToken, setRefreshToken] = useState(0);
  const mountedRef = useRef(true);
  const prevKeyRef = useRef(key);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      const next = getEntry<T>(key);
      if (next) {
        setDataState(next.data);
        setStale(Date.now() - next.savedAt >= staleMs);
        setError(null);
      } else {
        setDataState(null);
        setStale(false);
      }
    }
  }, [key, staleMs]);

  useEffect(() => {
    if (!enabled) return;

    const existing = getEntry<T>(key);
    const existingIsFresh = existing
      ? Date.now() - existing.savedAt < staleMs
      : false;

    if (existingIsFresh && refreshToken === 0) {
      if (data !== existing!.data) {
        setDataState(existing!.data);
      }
      setLoading(false);
      setStale(false);
      return;
    }

    if (existing) {
      if (data !== existing.data) setDataState(existing.data);
      setStale(true);
    }

    setLoading(!existing);

    const controller = new AbortController();

    fetcher(controller.signal)
      .then((result) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setEntry(key, result);
        setDataState(result);
        setStale(false);
        setError(null);
      })
      .catch((e) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setError(e?.message || 'Fetch failed');
      })
      .finally(() => {
        if (!controller.signal.aborted && mountedRef.current) {
          setLoading(false);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, refreshToken]);

  const refresh = useCallback(() => {
    invalidatePageCache(key);
    setRefreshToken((t) => t + 1);
  }, [key]);

  const setData = useCallback(
    (updater: T | ((prev: T | null) => T | null)) => {
      setDataState((prev) => {
        const next = typeof updater === 'function'
          ? (updater as (p: T | null) => T | null)(prev)
          : updater;
        if (next !== null) {
          setEntry(key, next);
        }
        return next;
      });
    },
    [key],
  );

  return { data, loading, error, stale, refresh, setData };
}
